/**
 * Um livro achado numa busca, ainda fora de qualquer lista.
 *
 * É o `BookSearchResult` de
 * `lib/features/books/data/models/book_search_result.dart`. Ele não é gravado
 * em lugar nenhum: nasce das três buscas (Open Library, Gutendex, Internet
 * Archive), vive na tela e só vira `Livro` quando alguém guarda.
 */

import { derivarRotuloDaFonte, livroNovo, pareceTextoPuro, type Livro } from './livro';

export interface ResultadoDeLivro {
  titulo: string;
  autor: string;
  capa: string;
  ano: string;
  idioma: string;
  editora: string;
  paginas: number;
  /** `epub`, `pdf`, `txt`... o formato principal para baixar. */
  formato: string;
  tamanhoMb: number | null;
  nota: number | null;
  votos: number | null;
  descricao: string;
  assuntos: string[];
  /** De onde veio: "Open Library", "Project Gutenberg", "Internet Archive". */
  acervo: string;
  obra: string;
  iaId: string;
  /** Zero quando não é do Projeto Gutenberg. */
  gutenbergId: number;
  /** Extensão em minúscula → endereço. */
  downloads: Record<string, string>;
  /**
   * As extensões que o item **tem mesmo**, sem endereço ainda.
   *
   * O `downloads` só se preenche quando se sabe o nome do arquivo, e o nome do
   * arquivo só vem da ficha do item. A busca, porém, já diz quais formatos
   * existem — então isto guarda o "tem epub" enquanto o "o epub se chama
   * assim" ainda não chegou.
   */
  formatos: string[];
  /**
   * Item de empréstimo: existe no acervo, mas nada nele se baixa nem se lê
   * direto. No Internet Archive é o `access-restricted-item`.
   */
  emprestado: boolean;
  /** Os endereços de texto puro que o leitor tenta, na ordem. */
  candidatos: string[];
}

/**
 * Se vale oferecer o botão de baixar.
 *
 * Mora aqui, e não em cada tela, porque a resposta tem três partes e as quatro
 * telas que perguntam erravam cada uma do seu jeito: a estante olhava só o
 * `downloads`, que nasce vazio; o detalhe respondia "sim" para todo item do
 * Archive, mesmo os de empréstimo.
 */
export function daParaBaixar(r: ResultadoDeLivro): boolean {
  if (r.emprestado) return false;
  return Object.keys(r.downloads).length > 0 || r.formatos.length > 0;
}

/**
 * `BookSearchResult.id`. Vale como chave de lista, e é o que o detalhe usa
 * para saber a qual acervo perguntar mais.
 */
export function idDoResultado(r: ResultadoDeLivro): string {
  if (r.obra) return r.obra;
  if (r.gutenbergId > 0) return `pg${r.gutenbergId}`;
  if (r.iaId) return r.iaId;
  return r.titulo;
}

/** `BookSearchResult.metaLine`: "pdf · English · 2020 · 2.9 MB". */
export function linhaMeta(r: ResultadoDeLivro): string {
  const partes: string[] = [];
  if (r.formato) partes.push(r.formato.toLowerCase());
  if (r.idioma) partes.push(r.idioma);
  if (r.ano) partes.push(r.ano);
  if (r.tamanhoMb != null && r.tamanhoMb > 0) partes.push(`${r.tamanhoMb.toFixed(1)} MB`);
  return partes.join(' · ');
}

/** `BookSearchResult.hasRating`. */
export const temNota = (r: ResultadoDeLivro): boolean => r.nota != null && r.nota > 0;

/**
 * `BookSearchResult.toBookItem`: o resultado vira o livro que se guarda.
 *
 * O `iaId` cai para `pg<id>` quando o livro é do Gutenberg e não tem item no
 * Archive — é assim que o leitor volta a montar os candidatos depois, a partir
 * só do que ficou gravado.
 *
 * E "o que ficou gravado" é um endereço só: o `Livro` tem um `fonte`, e a busca
 * chega com uma lista. O Flutter guarda o primeiro (`readCandidates.first`), e
 * o primeiro costuma ser a página do item no Archive — que é HTML, e que o
 * `resolverLeitura` descarta por não ser texto puro. Dom Casmurro chegava com
 * onze endereços, entre eles o texto inteiro do Gutenberg em português, e
 * mesmo assim abria como "só empréstimo": o único que sobreviveu à passagem
 * foi justamente o que não serve para ler. Por isso o texto puro vem na
 * frente, e o primeiro da lista só vale quando não há nenhum.
 */
export function resultadoParaLivro(r: ResultadoDeLivro, status = '', quem = ''): Livro {
  const iaId = r.iaId || (r.gutenbergId > 0 ? `pg${r.gutenbergId}` : '');
  const legivel = r.candidatos.find(pareceTextoPuro);
  return livroNovo({
    obra: r.obra,
    iaId,
    titulo: r.titulo,
    autor: r.autor,
    capa: r.capa,
    ano: r.ano,
    paginas: r.paginas,
    assuntos: r.assuntos,
    status,
    quem,
    quando: new Date(),
    fonte: legivel ?? (r.candidatos.length > 0 ? (r.candidatos[0] as string) : ''),
    rotuloDaFonte: r.acervo || derivarRotuloDaFonte(iaId),
  });
}

/** Um resultado com tudo vazio, para os mapeadores preencherem o que têm. */
export function resultadoNovo(campos: Partial<ResultadoDeLivro> = {}): ResultadoDeLivro {
  return {
    titulo: '',
    autor: '',
    capa: '',
    ano: '',
    idioma: '',
    editora: '',
    paginas: 0,
    formato: '',
    tamanhoMb: null,
    nota: null,
    votos: null,
    descricao: '',
    assuntos: [],
    acervo: '',
    obra: '',
    iaId: '',
    gutenbergId: 0,
    downloads: {},
    formatos: [],
    emprestado: false,
    candidatos: [],
    ...campos,
  };
}
